import React, { useState, useMemo } from "react";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WavesIcon from "@mui/icons-material/Waves";
import WarningIcon from "@mui/icons-material/Warning";
import { StatCard, LineChart, DonutChart, CombinedBarLineChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { sleepStats, sleepTrends, sleepStageDistribution, sleepHrPatterns } from "../data";

export const SleepAnalytics: React.FC = () => {
  const theme = useTheme();
  const [trendView, setTrendView] = useState("sleep-stages");

  const stageDistributionData = useMemo(() => [
    { ...sleepStageDistribution[0], color: "#1976D2" },
    { ...sleepStageDistribution[1], color: "#9C27B0" },
    { ...sleepStageDistribution[2], color: "#4DD0E1" },
    { ...sleepStageDistribution[3], color: theme.palette.warning.main },
  ], [theme]);

  const statCards = [
    { metric: sleepStats.sleep_performance, icon: BedtimeIcon },
    { metric: sleepStats.sleep_consistency, icon: CheckCircleIcon },
    { metric: sleepStats.sleep_efficiency, icon: WavesIcon },
    { metric: sleepStats.high_sleep_stress, icon: WarningIcon },
  ];

  const getTrendSeries = () => {
    switch (trendView) {
      case "sleep-stages":
        return [
          { dataKey: "sleep_stage_deep", label: "Deep", color: "#1976D2", area: true, stack: "sleep" },
          { dataKey: "sleep_stage_rem", label: "REM", color: "#9C27B0", area: true, stack: "sleep" },
          { dataKey: "sleep_stage_light", label: "Light", color: "#4DD0E1", area: true, stack: "sleep" },
          { dataKey: "sleep_stage_awake", label: "Awake", color: theme.palette.warning.main, area: true, stack: "sleep" },
        ];
      case "efficiency":
        return [{ dataKey: "sleep_efficiency", label: "Sleep Efficiency", color: theme.palette.success.main, area: true }];
      case "sleep-debt":
        return [{ dataKey: "sleep_debt", label: "Sleep Debt (hrs)", color: theme.palette.error.main, area: true }];
      case "sleep-stress":
        return [{ dataKey: "sleep_stress", label: "Sleep Stress", color: theme.palette.warning.main, area: true }];
      default:
        return [];
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Sleep Trends"
              subtitle="Daily sleep metrics over time."
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, v) => { if (v !== null) setTrendView(v); }}
                  size="small"
                >
                  <ToggleButton value="sleep-stages">Sleep Stages</ToggleButton>
                  <ToggleButton value="efficiency">Efficiency</ToggleButton>
                  <ToggleButton value="sleep-debt">Sleep Debt</ToggleButton>
                  <ToggleButton value="sleep-stress">Sleep Stress</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <LineChart
                data={sleepTrends}
                xAxisKey="label"
                series={getTrendSeries()}
                height={300}
                yAxisMin={0}
                yAxisMax={trendView === "sleep-stages" ? 100 : undefined}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Sleep Stage Distribution"
              subtitle="Average time spent in each sleep stage."
            >
              <DonutChart data={stageDistributionData} height={300} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Heart Rate & Sleep Efficiency by Day"
              subtitle="Weekly comparison of sleep heart rate and efficiency."
            >
              <CombinedBarLineChart
                data={sleepHrPatterns}
                xAxisKey="day"
                barSeries={[{ dataKey: "heart_rate", label: "Heart Rate (bpm)", color: theme.palette.info.main }]}
                lineSeries={[{ dataKey: "sleep_efficiency", label: "Sleep Efficiency (%)", color: theme.palette.success.main }]}
                height={300}
                barYAxisMin={40}
                barYAxisMax={80}
                lineYAxisMin={70}
                lineYAxisMax={100}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
